import React, { useCallback } from 'react';

import { FormattedMessage, type MessageDescriptor } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Badge from '@atlaskit/badge';
import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import Range from '@atlaskit/range';
import { token } from '@atlaskit/tokens';

import { useProductDependencies } from '../../controllers/use-product-dependencies';

const styles = cssMap({
	badgeContainer: {
		marginTop: token('space.negative.150'),
		width: 'max-content',
	},
	labelWrapper: {
		paddingTop: token('space.150'),
	},
	rangeContainer: {
		paddingRight: token('space.100'),
	},
	wrapper: {
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'space-between',
	},
});

type RangeFieldProps<TValue> = {
	analyticSource: string;
	label: MessageDescriptor;
	maxValue: number;
	minValue: number;
	prefix: string;
	step?: number;
	unit?: string;
	updateRangeValue(value: TValue): void;
	value?: TValue;
};

export const RangeField = <TValue extends number>({
	analyticSource,
	label,
	maxValue,
	minValue,
	prefix,
	step = 1,
	unit,
	updateRangeValue,
	value,
}: RangeFieldProps<TValue>): React.JSX.Element | null => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { submitError } = useProductDependencies();

	const handleRangeUpdate = useCallback(
		(newValue: number) => {
			updateRangeValue(newValue as TValue);
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'changed',
					actionSubject: 'range',
					actionSubjectId: `${prefix}NumberRange`,
					source: analyticSource,
					attributes: {
						value: newValue,
					},
				},
			}).fire();
		},
		[analyticSource, createAnalyticsEvent, prefix, updateRangeValue],
	);

	if (typeof value !== 'number') {
		const error = new Error(`${prefix} range value should be a number but got: ${typeof value}`);
		submitError?.(error);

		return null;
	}

	return (
		<Box testId="config-panel-range-field" xcss={styles.wrapper}>
			<Box xcss={styles.labelWrapper}>
				<label htmlFor="range-input">
					<FormattedMessage {...label} />
				</label>
			</Box>
			<Box xcss={styles.rangeContainer}>
				<Range
					id="range-input"
					onChange={handleRangeUpdate}
					testId="config-panel-range-field-range-input"
					value={value}
					step={step}
					min={minValue}
					max={maxValue}
				/>
				<Box
					xcss={styles.badgeContainer}
					style={{ paddingLeft: `${Math.min((100 * Math.max(value - 1, 0)) / maxValue, 90)}%` }}
				>
					<Badge>{value + (unit ? unit : '')}</Badge>
				</Box>
			</Box>
		</Box>
	);
};
